import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AnimationPicker } from './AnimationPicker';
import { ExerciseDemo } from './ExerciseDemo';
import { ExerciseMetaForm, EQUIPMENT_LABELS } from './ExerciseMetaForm';

const CATEGORY_LABELS = {
  strength: 'Siła',
  mobility: 'Mobilność',
  stretching: 'Stretching',
  breathing: 'Oddychanie',
  activation: 'Aktywacja',
  corrective: 'Korekcyjne',
  stability: 'Stabilizacja',
  cardio: 'Cardio',
};

const DIFFICULTY_LABELS = {
  beginner: 'Początkujący',
  intermediate: 'Średniozaawansowany',
  advanced: 'Zaawansowany',
};

// Shared exercise-instruction view — used by the exercise library, the plan
// builder (trainer picking/reviewing an exercise), and the client's plan
// view, so all three always show the same full instructions instead of the
// truncated one-line description that used to be the only thing visible.
// Pass onAnimationChange (trainer views) to show the animation picker, and
// onMetaSaved (exercise library) to show the plan-generator fields form.
export const ExerciseDetailDialog = ({ exercise, open, onOpenChange, onAnimationChange, onMetaSaved, allExercises }) => {
  if (!exercise) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-text text-2xl text-white">{exercise.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <ExerciseDemo exercise={exercise} />

          <div className="flex items-center gap-3 flex-wrap">
            <span className="label-text text-[#C9A84C] text-[0.65rem] px-3 py-1 rounded-full border border-[#C9A84C]/30">
              {CATEGORY_LABELS[exercise.category] || exercise.category}
            </span>
            <span className="label-text text-[#A1A1A1] text-[0.65rem] px-3 py-1 rounded-full border border-[#2A2A2A]">
              {DIFFICULTY_LABELS[exercise.difficulty] || exercise.difficulty}
            </span>
            {(exercise.equipment || []).map((eq, i) => (
              <span key={i} className="text-[0.65rem] px-3 py-1 rounded-full bg-[#0A0A0A] text-[#A1A1A1]">
                {EQUIPMENT_LABELS[eq] || eq}
              </span>
            ))}
          </div>

          {exercise.description && (
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">OPIS</label>
              <p className="body-text text-white">{exercise.description}</p>
            </div>
          )}

          {exercise.execution_steps?.length > 0 && (
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">JAK WYKONAĆ</label>
              <ol className="space-y-2">
                {exercise.execution_steps.map((step, i) => (
                  <li key={i} className="body-text text-white text-sm flex gap-3">
                    <span className="text-[#C9A84C] font-bold shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(exercise.tempo || exercise.sets_range || exercise.reps_range || exercise.rest_seconds) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {exercise.tempo && (
                <div>
                  <div className="label-text text-[#A1A1A1] text-[0.6rem] mb-1">TEMPO</div>
                  <div className="text-white text-sm">{exercise.tempo}</div>
                </div>
              )}
              {exercise.sets_range && (
                <div>
                  <div className="label-text text-[#A1A1A1] text-[0.6rem] mb-1">SERIE</div>
                  <div className="text-white text-sm">{exercise.sets_range}</div>
                </div>
              )}
              {exercise.reps_range && (
                <div>
                  <div className="label-text text-[#A1A1A1] text-[0.6rem] mb-1">POWTÓRZENIA</div>
                  <div className="text-white text-sm">{exercise.reps_range}</div>
                </div>
              )}
              {exercise.rest_seconds && (
                <div>
                  <div className="label-text text-[#A1A1A1] text-[0.6rem] mb-1">PRZERWA</div>
                  <div className="text-white text-sm">{exercise.rest_seconds}s</div>
                </div>
              )}
            </div>
          )}

          {exercise.cues?.length > 0 && (
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">WSKAZÓWKI</label>
              <ul className="space-y-1">
                {exercise.cues.map((cue, i) => (
                  <li key={i} className="body-text text-white text-sm flex gap-2">
                    <span className="text-[#C9A84C]">•</span>
                    <span>{cue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(exercise.muscles_targeted?.length > 0) && (
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">ZAANGAŻOWANE MIĘŚNIE</label>
              <div className="flex flex-wrap gap-2">
                {exercise.muscles_targeted.map((m, i) => (
                  <span key={i} className="px-3 py-1 bg-[#0A0A0A] rounded-full text-xs text-[#A1A1A1]">{m}</span>
                ))}
              </div>
            </div>
          )}

          {exercise.contraindications?.length > 0 && (
            <div>
              <label className="label-text text-red-400 mb-2 block">PRZECIWWSKAZANIA</label>
              <div className="flex flex-wrap gap-2">
                {exercise.contraindications.map((c, i) => (
                  <span key={i} className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-xs text-red-300">{c}</span>
                ))}
              </div>
            </div>
          )}

          {exercise.variations?.length > 0 && (
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">WARIANTY</label>
              <ul className="space-y-1">
                {exercise.variations.map((v, i) => (
                  <li key={i} className="body-text text-white text-sm">• {v}</li>
                ))}
              </ul>
            </div>
          )}

          {onMetaSaved && (
            <div className="pt-4 border-t border-[#2A2A2A]">
              <ExerciseMetaForm key={exercise.id} exercise={exercise} allExercises={allExercises} onSaved={onMetaSaved} />
            </div>
          )}

          {onAnimationChange && (
            <div className="pt-4 border-t border-[#2A2A2A]">
              <AnimationPicker key={exercise.id} exercise={exercise} onChange={onAnimationChange} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
